import { useCallback, useEffect, useState } from 'react'
import { notifyProfileCompletionChanged } from '@/features/company-profile/companyProfileCompletionBus'
import { createIcp, deleteIcp, listIcps, setPrimaryIcp, updateIcp } from '../services/icpsApi'
import type { Icp, IcpInput } from '../types'

// Primary first, then oldest first — the order the API returns.
function sortIcps(icps: Icp[]): Icp[] {
  return [...icps].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.createdAt.localeCompare(b.createdAt))
}

export function useIcps(workspaceId: string) {
  const [icps, setIcps] = useState<Icp[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listIcps(workspaceId)
      .then((result) => { if (!cancelled) setIcps(sortIcps(result)) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load ICPs') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [workspaceId])

  useEffect(() => load(), [load])

  // An ICP change can complete (or un-complete) the workspace's profile, so the
  // always-visible profile banner is told to recheck — same bus the profile form uses.
  const create = useCallback(
    async (input: IcpInput) => {
      const created = await createIcp(workspaceId, input)
      setIcps((prev) => sortIcps([...prev, created]))
      notifyProfileCompletionChanged()
      return created
    },
    [workspaceId],
  )

  const update = useCallback(
    async (icpId: string, input: IcpInput) => {
      const updated = await updateIcp(workspaceId, icpId, input)
      setIcps((prev) => sortIcps(prev.map((icp) => (icp.id === icpId ? updated : icp))))
      notifyProfileCompletionChanged()
      return updated
    },
    [workspaceId],
  )

  const remove = useCallback(
    async (icpId: string) => {
      await deleteIcp(workspaceId, icpId)
      setIcps((prev) => prev.filter((icp) => icp.id !== icpId))
      notifyProfileCompletionChanged()
    },
    [workspaceId],
  )

  const makePrimary = useCallback(
    async (icpId: string) => {
      const promoted = await setPrimaryIcp(workspaceId, icpId)
      setIcps((prev) => sortIcps(prev.map((icp) => ({ ...icp, isPrimary: icp.id === promoted.id }))))
    },
    [workspaceId],
  )

  return { icps, loading, error, reload: load, create, update, remove, makePrimary }
}
